import { z } from 'zod';
import { PhoneNumberSchema } from '../common/phone-number.schema';
import { refersTo } from '../common/refers-to';

// Agent-profile domain constants. Values are English UPPER_CASE per the
// union-types convention; the user-facing labels live only in `@immo/i18n` and
// are mapped from these codes on the client. The lists are deliberately fixed
// and Exposé-ready, not the predecessor's structured activity×segment
// modelling — see IMM-221 Resolved Decisions 3 + 4.
export const MemberSalutationSchema = z.enum(['MR', 'MRS', 'DIVERSE']);

export type MemberSalutation = z.infer<typeof MemberSalutationSchema>;

export const SpecialisationSchema = z.enum(['RESIDENTIAL', 'COMMERCIAL', 'INVESTMENT', 'NEW_BUILD', 'LAND', 'RENTAL']);

export type Specialisation = z.infer<typeof SpecialisationSchema>;

export const ProfileLanguageSchema = z.enum(['DE', 'EN', 'FR', 'ES', 'IT', 'TR', 'RU', 'PL']);

export type ProfileLanguage = z.infer<typeof ProfileLanguageSchema>;

const emptyToNull = (value: unknown): unknown => (typeof value === 'string' && value.trim() === '' ? null : value);

// Optional free-text field: an empty/whitespace submission means "not set"
// (→ null), a present value is trimmed and length-checked. `.default(null)`
// lets the key be omitted entirely.
const optionalText = (max: number) =>
  z.preprocess(emptyToNull, z.string().trim().min(1).max(max).nullable()).default(null);

const optionalPhone = z.preprocess(emptyToNull, PhoneNumberSchema.nullable()).default(null);

// A name part. Optional in the PATCH sense `avatarMediaId` uses -- omitted
// leaves the stored name untouched -- because the onboarding wizard saves on
// every step advance but only collects the name partway through, and a member
// with no name yet must not be blocked from starting. What it will not accept
// is a blank: a present name is trimmed and must be real, since avatars compose
// their initials from it and an empty one degrades them to `?` (IMM-426).
const optionalName = z.string().trim().min(1).max(80).optional();

const HttpUrlSchema = z.url().refine((value) => /^https?:\/\//i.test(value), { message: 'must be an http(s) URL' });

// Profile-photo policy. The photo itself is stored by the media module
// (IMM-280) as a generic file; these are the consumer-owned constraints the
// caller passes to media's create-upload and the server re-checks when the
// uploaded file is attached to the profile. Shared so the web form and the
// backend agree on one source of truth.
export const MEMBER_AVATAR_ALLOWED_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MEMBER_AVATAR_MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const MEMBER_AVATAR_DESCRIPTION = 'member-profile-photo';

export const MemberProfileResponseSchema = z.object({
  memberId: refersTo('member', z.uuid()),
  workspaceId: z.uuid(),
  displayName: z.string(),
  // The name's constituent parts, so the profile form can edit them and an
  // avatar can compose initials without re-splitting `displayName` (which falls
  // back to the e-mail when no name is set). Empty string when unset — the same
  // shape `WorkspaceMemberSchema` uses for a member with no name yet.
  firstName: z.string(),
  lastName: z.string(),
  email: z.email(),
  // The media file id of the profile photo (IMM-280), or null when none is
  // set. The client resolves a display URL via the media read endpoint; the
  // id is also the branding handle Exposé creation (IMM-220) consumes.
  avatarMediaId: z.uuid().nullable(),
  salutation: MemberSalutationSchema.nullable(),
  academicTitle: z.string().nullable(),
  position: z.string().nullable(),
  phone: z.string().nullable(),
  mobile: z.string().nullable(),
  bio: z.string().nullable(),
  specialisations: z.array(SpecialisationSchema),
  languages: z.array(ProfileLanguageSchema),
  certifications: z.array(z.string()),
  socialLinks: z.array(z.string()),
  // Derived from the member's primary location (IMM-20); null until that
  // primary-location-per-member source exists — the profile carries no
  // location picker of its own.
  location: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});

export type MemberProfileResponse = z.infer<typeof MemberProfileResponseSchema>;

const memberProfileFieldsShape = {
  // A confirmed media file id to set as the photo, or null to remove it.
  // Omitted → the photo is left unchanged. The server re-validates the file
  // against the photo policy and is authoritative.
  avatarMediaId: z.uuid().nullable().optional(),
  salutation: z.preprocess(emptyToNull, MemberSalutationSchema.nullable()).default(null),
  academicTitle: optionalText(40),
  position: optionalText(80),
  phone: optionalPhone,
  mobile: optionalPhone,
  bio: optionalText(600),
  specialisations: z.array(SpecialisationSchema).default([]),
  languages: z.array(ProfileLanguageSchema).default([]),
  certifications: z.array(z.string().trim().min(1).max(120)).default([]),
  socialLinks: z.array(HttpUrlSchema).default([]),
};

/**
 * Admin edit of *another* member's profile. Carries no name: a member's name is
 * theirs to set (see `UpdateOwnMemberProfileBodySchema`), and admin name edits
 * stay on the member-update endpoint, so exactly one endpoint owns each write.
 */
export const UpdateMemberProfileBodySchema = z.object(memberProfileFieldsShape).strict();

export type UpdateMemberProfileBody = z.infer<typeof UpdateMemberProfileBodySchema>;

/**
 * Self-serve profile update: the shared profile fields plus the member's own
 * name. The name is written through to the IdP so the identity provider stays
 * the source of truth and a later sign-in refresh returns what the member set
 * rather than overwriting it (IMM-426).
 */
export const UpdateOwnMemberProfileBodySchema = z
  .object({
    ...memberProfileFieldsShape,
    firstName: optionalName,
    lastName: optionalName,
  })
  .strict();

export type UpdateOwnMemberProfileBody = z.infer<typeof UpdateOwnMemberProfileBodySchema>;

export const MyMemberProfilePathParamsSchema = z.object({ workspaceId: z.uuid() });

export const GetMemberProfilePathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });

// Avatar upload — a constrained wrapper over the generic media upload flow that
// lets any member upload their own profile photo without holding
// organization:media:file-write. The server fixes description, allowedContentTypes,
// and maxSizeBytes; the caller only supplies the file's declared type, size,
// and optional filename.
export const CreateMemberAvatarUploadPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const CreateMemberAvatarUploadBodySchema = z
  .object({
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    originalFilename: z.string().trim().min(1).max(255).nullish(),
  })
  .strict();

export type CreateMemberAvatarUploadBody = z.infer<typeof CreateMemberAvatarUploadBodySchema>;

export const ConfirmMemberAvatarUploadPathParamsSchema = z.object({ workspaceId: z.uuid(), fileId: z.uuid() });

export const ReadMemberAvatarPathParamsSchema = z.object({ workspaceId: z.uuid(), fileId: z.uuid() });
